import { useCallback, useMemo, useState } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { useDeleteUpload, useUploadImages, useUploads } from '@/api';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
  State,
} from '@/components/atoms';
import { TabsContent } from '@/components/molecules';
import { isImageUpload } from '@/lib/helpers/product-images';
import { getUploadIdentifier } from '@/lib/helpers/upload';
import { cn } from '@/lib/utils';
import type { Upload } from '@/types';
import { Copy, Eye, ImagePlus, Trash2, UploadCloud } from 'lucide-react';
import toast from 'react-hot-toast';

const MAX_FILE_SIZE_MB = 2;

export const ProductUploadsTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [previewUpload, setPreviewUpload] = useState<Upload | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Upload | null>(null);
  const { data: uploads = [], isLoading } = useUploads();
  const uploadMutation = useUploadImages();
  const deleteMutation = useDeleteUpload();

  const imageUploads = useMemo(
    () =>
      uploads.filter((upload) =>
        isImageUpload(upload.metaData?.mimetype, upload.metaData?.filename)
      ),
    [uploads]
  );

  const filteredUploads = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return imageUploads;

    return imageUploads.filter((upload) => {
      const filename = upload.metaData?.filename?.toLowerCase() ?? '';
      return (
        filename.includes(query) ||
        String(upload.id).includes(query) ||
        upload.url.toLowerCase().includes(query)
      );
    });
  }, [imageUploads, searchQuery]);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        toast.error(`Maximum file size is ${MAX_FILE_SIZE_MB}MB per file.`);
        return;
      }

      if (acceptedFiles.length === 0) return;

      void uploadMutation.mutateAsync({ files: acceptedFiles });
    },
    [uploadMutation]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    maxSize: MAX_FILE_SIZE_MB * 1024 * 1024,
    multiple: true,
    noClick: true,
    noKeyboard: true,
  });

  const copyUploadId = async (id: number) => {
    try {
      await navigator.clipboard.writeText(String(id));
      toast.success(`Copied image ID ${id}`);
    } catch {
      toast.error('Unable to copy image ID');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteMutation.mutateAsync(getUploadIdentifier(deleteTarget));
      if (previewUpload?.id === deleteTarget.id) {
        setPreviewUpload(null);
      }
      setDeleteTarget(null);
    } catch {
      // Errors are surfaced by the global mutation handler.
    }
  };

  return (
    <TabsContent value="gallery" className="space-y-6">
      <Card className="border shadow-none">
        <CardContent className="space-y-4 pt-6">
          <div>
            <h3 className="font-semibold">Product media library</h3>
            <p className="text-muted-foreground text-sm">
              Upload product images here first, then attach them by ID when
              creating products or completing a bulk upload.
            </p>
          </div>

          <div
            {...getRootProps({
              className: cn(
                'rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors',
                isDragActive
                  ? 'border-primary bg-primary/5'
                  : 'border-input bg-accent/20'
              ),
            })}
          >
            <input {...getInputProps()} />
            <div className="mx-auto flex max-w-md flex-col items-center gap-3">
              <div className="rounded-full bg-primary/10 p-3 text-primary">
                <UploadCloud className="size-6" />
              </div>
              <div className="space-y-1">
                <p className="font-medium">Drag and drop product images here</p>
                <p className="text-muted-foreground text-sm">or</p>
              </div>
              <Button
                type="button"
                variant="outline"
                isLoading={uploadMutation.isPending}
                onClick={open}
              >
                <ImagePlus />
                Upload Images
              </Button>
              <p className="text-muted-foreground text-xs">
                PNG, JPG, WEBP · up to {MAX_FILE_SIZE_MB}MB each
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Input
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Search gallery by name or ID..."
          className="sm:max-w-md"
        />
        <Badge variant="outline">{filteredUploads.length} images</Badge>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-12 items-stretch gap-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton
              key={index}
              className="col-span-12 aspect-[4/5] rounded-xl sm:col-span-6 lg:col-span-3"
            />
          ))}
        </div>
      ) : filteredUploads.length === 0 ? (
        <State
          heading="No images yet"
          description="Upload product images to your gallery."
        />
      ) : (
        <div className="grid grid-cols-12 items-stretch gap-3">
          {filteredUploads.map((upload) => (
            <Card
              key={upload.id}
              className="col-span-12 flex h-full flex-col overflow-hidden border py-0 shadow-none sm:col-span-6 lg:col-span-3"
            >
              <button
                type="button"
                className="group relative aspect-square shrink-0 overflow-hidden bg-muted/20"
                onClick={() => setPreviewUpload(upload)}
                aria-label={`View ${upload.metaData?.filename || `upload ${upload.id}`}`}
              >
                <img
                  src={upload.url}
                  alt={upload.metaData?.filename || `Upload ${upload.id}`}
                  className="size-full object-cover transition-transform duration-200 group-hover:scale-105"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/35">
                  <span className="flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-medium text-foreground opacity-0 transition-opacity group-hover:opacity-100">
                    <Eye className="size-3.5" />
                    View
                  </span>
                </div>
              </button>
              <CardContent className="flex flex-1 flex-col gap-3 py-4">
                <div className="min-h-10 min-w-0">
                  <p className="truncate text-sm font-medium">
                    {upload.metaData?.filename || `Image ${upload.id}`}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    ID {upload.id}
                  </p>
                </div>
                <div className="mt-auto flex flex-col gap-2">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      onClick={() => setPreviewUpload(upload)}
                      aria-label={`View ${upload.metaData?.filename || `upload ${upload.id}`}`}
                    >
                      <Eye className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(upload)}
                      aria-label={`Delete ${upload.metaData?.filename || `upload ${upload.id}`}`}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => void copyUploadId(upload.id)}
                  >
                    <Copy />
                    Copy ID
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={!!previewUpload}
        onOpenChange={(open) => {
          if (!open) setPreviewUpload(null);
        }}
      >
        <DialogContent className="gap-4 sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {previewUpload?.metaData?.filename ||
                `Image ${previewUpload?.id}`}
            </DialogTitle>
            <DialogDescription>
              ID {previewUpload?.id}
              {previewUpload?.metaData?.extension
                ? ` · ${previewUpload.metaData.extension.toUpperCase()}`
                : ''}
            </DialogDescription>
          </DialogHeader>
          {previewUpload && (
            <div className="overflow-hidden rounded-xl border bg-muted/20">
              <img
                src={previewUpload.url}
                alt={
                  previewUpload.metaData?.filename ||
                  `Upload ${previewUpload.id}`
                }
                className="max-h-[70vh] w-full object-contain"
              />
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                previewUpload && void copyUploadId(previewUpload.id)
              }
            >
              <Copy />
              Copy ID
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPreviewUpload(null)}
              >
                Close
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  if (!previewUpload) return;
                  setDeleteTarget(previewUpload);
                  setPreviewUpload(null);
                }}
              >
                <Trash2 />
                Delete
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete upload?</DialogTitle>
            <DialogDescription>
              This will permanently delete{' '}
              {deleteTarget?.metaData?.filename
                ? `"${deleteTarget.metaData.filename}"`
                : `image ID ${deleteTarget?.id}`}
              . Products already using this image will keep their existing
              references until updated.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={deleteMutation.isPending}
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              isLoading={deleteMutation.isPending}
              onClick={() => void handleConfirmDelete()}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
};
